import { useState, useRef, useCallback } from 'react';

export interface TemplateVariable {
  name: string;
  label: string;
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { name: '{First_Name}', label: 'First Name' },
  { name: '{Last_Name}', label: 'Last Name' },
  { name: '{Company_Name}', label: 'Company Name' },
  { name: '{Company}', label: 'Company' },
  { name: '{Agent_Name}', label: 'Agent Name' },
  { name: '{Your_Company}', label: 'Your Company' },
  { name: '{Meeting_Date}', label: 'Meeting Date' },
  { name: '{Meeting_Time}', label: 'Meeting Time' },
  { name: '{Meeting_Link}', label: 'Meeting Link' },
];

interface UseVariableInsertionOptions {
  onContentChange: (newContent: string) => void;
  getContent: () => string;
}

export function useVariableInsertion({ onContentChange, getContent }: UseVariableInsertionOptions) {
  const [showVariables, setShowVariables] = useState(false);
  const [insertedVar, setInsertedVar] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPosRef = useRef<number>(0);
  const activeFieldRef = useRef<'textarea' | 'input'>('textarea');

  const trackCursor = useCallback((field: 'textarea' | 'input' = 'textarea') => {
    activeFieldRef.current = field;
    const el = field === 'textarea' ? textareaRef.current : inputRef.current;
    if (el) {
      cursorPosRef.current = el.selectionStart ?? 0;
    }
  }, []);

  const handleInsertVariable = useCallback((varName: string) => {
    const field = activeFieldRef.current;
    const el = field === 'textarea' ? textareaRef.current : inputRef.current;
    const currentContent = getContent();
    const pos = cursorPosRef.current;
    const before = currentContent.slice(0, pos);
    const after = currentContent.slice(pos);
    const newContent = before + varName + after;
    onContentChange(newContent);

    setInsertedVar(varName);
    setTimeout(() => setInsertedVar(null), 600);

    requestAnimationFrame(() => {
      if (el) {
        const newPos = pos + varName.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
        cursorPosRef.current = newPos;
      }
    });
  }, [getContent, onContentChange]);

  return {
    showVariables,
    setShowVariables,
    insertedVar,
    textareaRef,
    inputRef,
    cursorPosRef,
    activeFieldRef,
    trackCursor,
    handleInsertVariable,
  };
}
